import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/meta";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserCircle, Building2, ShieldCheck, LogOut, KeyRound, Loader2 } from "lucide-react";
import { PasswordInput } from "@/components/PasswordInput";
import { toast } from "sonner";

export default function MinhaConta() {
  const { user, logout } = useAuth();
  const [cur, setCur] = useState("");
  const [nova, setNova] = useState("");
  const [conf, setConf] = useState("");
  const [saving, setSaving] = useState(false);

  const empresa = user.empresa_nome || "—";
  const myUnits = user.all_units
    ? ["Todas as unidades"]
    : (user.unidades || []).map((u) => `${u.codigo} — ${u.nome}`);

  const trocarSenha = async () => {
    if (nova.length < 6) return toast.error("Nova senha: mínimo 6 caracteres");
    if (nova !== conf) return toast.error("As senhas não conferem");
    setSaving(true);
    try { await api.post("/auth/change-password", { current: cur, new: nova }); toast.success("Senha alterada com sucesso"); setCur(""); setNova(""); setConf(""); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };

  const Row = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-3 py-2 border-b border-border">
      <Icon className="w-5 h-5 text-primary mt-0.5" />
      <div><div className="text-[10px] uppercase text-muted-foreground">{label}</div><div className="font-medium">{value}</div></div>
    </div>
  );

  return (
    <div className="space-y-5 max-w-2xl" data-testid="minha-conta-page">
      <div>
        <div className="text-xs text-primary uppercase tracking-[0.18em] font-bold">Sessão</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase flex items-center gap-2"><UserCircle className="w-8 h-8 text-primary" /> Minha Conta</h1>
      </div>

      <Card className="bg-card/70 p-5">
        <Row icon={UserCircle} label="Nome" value={user.name} />
        <Row icon={ShieldCheck} label="Perfil" value={ROLE_LABELS[user.role] || user.role} />
        <Row icon={Building2} label="Empresa" value={empresa} />
        <div className="flex items-start gap-3 py-2">
          <Building2 className="w-5 h-5 text-primary mt-0.5" />
          <div>
            <div className="text-[10px] uppercase text-muted-foreground">Unidades com acesso</div>
            <div className="flex flex-wrap gap-1.5 mt-1" data-testid="minhas-unidades">
              {myUnits.length ? myUnits.map((u) => <span key={u} className="text-xs font-mono bg-secondary/60 border border-border rounded-sm px-2 py-0.5">{u}</span>) : <span className="text-muted-foreground">—</span>}
            </div>
          </div>
        </div>
      </Card>

      <Card className="bg-card/70 p-5 space-y-3">
        <div className="font-display text-lg font-bold uppercase flex items-center gap-2"><KeyRound className="w-5 h-5 text-primary" /> Alterar Senha</div>
        <div><Label>Senha atual</Label><PasswordInput value={cur} onChange={(e) => setCur(e.target.value)} data-testid="mc-senha-atual" /></div>
        <div><Label>Nova senha</Label><PasswordInput value={nova} onChange={(e) => setNova(e.target.value)} data-testid="mc-senha-nova" /></div>
        <div><Label>Confirmar nova senha</Label><PasswordInput value={conf} onChange={(e) => setConf(e.target.value)} data-testid="mc-senha-conf" /></div>
        <Button onClick={trocarSenha} disabled={saving} data-testid="btn-trocar-senha">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar nova senha"}</Button>
      </Card>

      <Button variant="outline" className="text-red-400 border-red-500/40" onClick={logout} data-testid="mc-logout"><LogOut className="w-4 h-4" /> Sair da conta</Button>
    </div>
  );
}
